import { NestApiType, nestCall } from '@/composables/nestApi';
import type { SchemaItem } from '@/composables/custom-data/customData';
import { formatAmount, formatDate } from '@/components/table-rest-api/formatters';
import { SpmFilterOperator } from '@/components/table-rest-api/filters';
import { AvailableTypeEnum } from '@/composables/custom-data/customData.interfaces';
import {
  getSystemRelationSchemaDefinition,
} from '@/composables/custom-data/systemRelationSchemas';
import type { SystemRelationSchemaName } from '@/composables/custom-data/systemRelationSchemas';

export interface CustomObjectRelationDefinition {
  sourceField: string;
  targetSchemaType: 'system' | 'custom';
  targetSchema: string;
  targetField: string;
}

export interface RelationPreviewField {
  key: string;
  label: string;
  value: string;
  fieldType?: 'text' | 'external_link';
  href?: string;
  dataType?: string;
  displayMode?: 'inline' | 'multiline';
}

export interface RelationPreviewResult {
  title: string;
  fields: RelationPreviewField[];
}

interface ResolveRelationPreviewParams {
  relation: CustomObjectRelationDefinition;
  relationValue: string | number;
  customSchemas: SchemaItem[];
  forceRefresh?: boolean;
}

const MAX_CUSTOM_MAIN_FIELDS = 4;

const relationPreviewCache = new Map<string, RelationPreviewResult | null>();

const SYSTEM_FIELDS_BY_SCHEMA: Record<string, Array<{ key: string; candidates: string[] }>> = {
  contacts: [
    { key: 'contact_last_name', candidates: ['contact_last_name', 'last_name'] },
    { key: 'contact_first_name', candidates: ['contact_first_name', 'first_name'] },
    { key: 'contact_data_email', candidates: ['contact_data_email', 'email'] },
  ],
  products: [
    { key: 'reference', candidates: ['reference'] },
    { key: 'name', candidates: ['name'] },
    { key: 'price', candidates: ['price'] },
    { key: 'link', candidates: ['link'] },
  ],
  // Cible attribut contacts.group_id : la preview montre le groupe du
  // référentiel (endpoint /customers-groups), pas un contact.
  'contacts:group_id': [
    { key: 'group_id', candidates: ['group_id', 'id_group'] },
    { key: 'name', candidates: ['name'] },
    { key: 'lang', candidates: ['lang'] },
  ],
};

const CUSTOM_CREATED_AT_KEYS = ['__meta_created_at', 'created_at', 'date_creation'] as const;
const CUSTOM_UPDATED_AT_KEYS = ['__meta_updated_at', 'updated_at', 'date_modification'] as const;

const toLabel = (value: string): string => value
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object'
  && value !== null
  && !Array.isArray(value)
);

const isDateKey = (key: string): boolean => /date|created_at|updated_at|modification|birthday/i.test(key);

const isScalar = (value: unknown): value is string | number | boolean => (
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
);

const isTechnicalIdKey = (key: string): boolean => /(^id$|^id_|_id$)/i.test(key);
const isMetaKey = (key: string): boolean => key.startsWith('__meta_');
const isAllowedCustomMetaKey = (key: string): boolean => (
  key === '__meta_created_at' || key === '__meta_updated_at'
);

const normalizeRecord = (input: unknown): Record<string, unknown> | null => {
  if (!input) return null;

  const payload = isPlainObject(input) ? input : null;
  if (!payload) return null;

  const data = payload.data;
  if (Array.isArray(data)) {
    return isPlainObject(data[0]) ? data[0] : null;
  }

  if (isPlainObject(data)) {
    return data;
  }

  return payload;
};

const flattenRecord = (record: Record<string, unknown>): Record<string, unknown> => {
  const flattened: Record<string, unknown> = { ...record };
  Object.entries(record).forEach(([key, value]) => {
    if (!isPlainObject(value)) return;
    if (key.endsWith('_data') || key === 'contact_data') {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        if (!(nestedKey in flattened)) {
          flattened[nestedKey] = nestedValue;
        }
      });
    }
  });
  return flattened;
};

const formatValue = (
  key: string,
  value: unknown,
  dataType?: string,
): string => {
  if (value === null || value === undefined) return '';

  if (dataType === AvailableTypeEnum.JSON || dataType === AvailableTypeEnum.GEOLOCATION) {
    if (typeof value === 'string') return value;
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }

  if (dataType === AvailableTypeEnum.DATE) {
    return formatDate(String(value), 'date');
  }

  if (dataType === AvailableTypeEnum.DATETIME) {
    return formatDate(String(value), 'datetime');
  }

  if (dataType === AvailableTypeEnum.BOOL) {
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    if (String(value) === '1') return 'true';
    if (String(value) === '0') return 'false';
  }

  if (isDateKey(key)) return formatDate(String(value), 'datetime');
  return String(value);
};

const buildCustomFieldLabelMap = (schema: SchemaItem | undefined): Map<string, string> => {
  const map = new Map<string, string>();
  if (!schema?.fields || !Array.isArray(schema.fields)) return map;

  schema.fields.forEach((field: any) => {
    if (field?.name) {
      map.set(field.name, field.label || toLabel(field.name));
    }
  });
  return map;
};

const buildCustomFieldTypeMap = (schema: SchemaItem | undefined): Map<string, string> => {
  const map = new Map<string, string>();
  if (!schema?.fields || !Array.isArray(schema.fields)) return map;

  schema.fields.forEach((field: any) => {
    if (field?.name && field?.type) {
      map.set(field.name, field.type);
    }
  });

  return map;
};

const toPreviewField = (
  key: string,
  value: unknown,
  fieldLabelMap: Map<string, string>,
  fieldTypeMap?: Map<string, string>,
  sourceRecord?: Record<string, unknown>,
): RelationPreviewField => {
  const isProductLink = key === 'link' && typeof value === 'string';
  let hrefValue: string | undefined;
  if (isProductLink && typeof value === 'string') {
    hrefValue = value;
  }
  const dataType = fieldTypeMap?.get(key);
  const displayMode = (
    dataType === AvailableTypeEnum.JSON
    || dataType === AvailableTypeEnum.LONGTEXT
    || dataType === AvailableTypeEnum.GEOLOCATION
  ) ? 'multiline' : 'inline';
  const formattedValue = key === 'price' && (typeof value === 'string' || typeof value === 'number')
    ? formatAmount(
      value as string | number,
      (typeof sourceRecord?.currency === 'string' && sourceRecord.currency) ? sourceRecord.currency : 'EUR',
    )
    : formatValue(key, value, dataType);
  return {
    key,
    label: fieldLabelMap.get(key) || toLabel(key),
    value: formattedValue,
    fieldType: isProductLink ? 'external_link' : 'text',
    href: hrefValue,
    dataType,
    displayMode,
  };
};

const getScalarValue = (record: Record<string, unknown>, key: string): string | number | boolean | undefined => {
  const value = record[key];
  return isScalar(value) ? value : undefined;
};

const getCustomFieldValue = (
  record: Record<string, unknown>,
  key: string,
  dataType?: string,
): unknown => {
  const value = record[key];
  if (value === null || value === undefined) return undefined;
  if (isScalar(value)) return value;

  if (dataType === AvailableTypeEnum.JSON || dataType === AvailableTypeEnum.GEOLOCATION) {
    return value;
  }

  return undefined;
};

const pickCustomPreviewFields = (
  record: Record<string, unknown>,
  relation: CustomObjectRelationDefinition,
  customSchema: SchemaItem | undefined,
  fieldLabelMap: Map<string, string>,
  fieldTypeMap: Map<string, string>,
): RelationPreviewField[] => {
  const schemaFields = Array.isArray(customSchema?.fields)
    ? customSchema!.fields.map((field: any) => ({
      name: field?.name,
      type: field?.type,
    })).filter((field: { name?: string }) => !!field.name)
    : Object.keys(record).map((fieldName) => ({ name: fieldName, type: undefined }));

  const mainFields: RelationPreviewField[] = [];

  schemaFields.forEach((field) => {
    const fieldName = field.name as string;
    if (mainFields.length >= MAX_CUSTOM_MAIN_FIELDS) return;
    if (isTechnicalIdKey(fieldName)) return;
    if (isMetaKey(fieldName)) return;
    if (fieldName === relation.targetField) return;

    const value = getCustomFieldValue(record, fieldName, field.type);
    if (value === undefined) return;
    if (typeof value === 'string' && value.trim() === '') return;

    if (field.type) {
      fieldTypeMap.set(fieldName, field.type);
    }
    mainFields.push(toPreviewField(fieldName, value, fieldLabelMap, fieldTypeMap, record));
  });

  const appendDateField = (candidateKeys: readonly string[]) => {
    const dateKey = candidateKeys.find((key) => getScalarValue(record, key) !== undefined);
    if (!dateKey) return;

    const value = getScalarValue(record, dateKey);
    if (value === undefined) return;

    mainFields.push(toPreviewField(dateKey, value, fieldLabelMap, fieldTypeMap, record));
  };

  appendDateField(CUSTOM_CREATED_AT_KEYS);
  appendDateField(CUSTOM_UPDATED_AT_KEYS);

  return mainFields;
};

const pickSystemPreviewFields = (
  record: Record<string, unknown>,
  relation: CustomObjectRelationDefinition,
  fieldLabelMap: Map<string, string>,
): RelationPreviewField[] => {
  // Une cible attribut peut avoir sa propre configuration de preview
  // (ex. contacts:group_id → champs du référentiel des groupes).
  const configuredFields = SYSTEM_FIELDS_BY_SCHEMA[`${relation.targetSchema}:${relation.targetField}`]
    || SYSTEM_FIELDS_BY_SCHEMA[relation.targetSchema]
    || [];
  const result: RelationPreviewField[] = [];

  configuredFields.forEach((fieldConfig) => {
    const matchedFieldName = fieldConfig.candidates.find((candidate) => getScalarValue(record, candidate) !== undefined);
    if (!matchedFieldName) return;

    const value = getScalarValue(record, matchedFieldName);
    if (value === undefined || String(value).trim() === '') return;

    // Keep canonical key for translation mapping while reading actual data from aliases.
    result.push(toPreviewField(fieldConfig.key, value, fieldLabelMap, undefined, record));
  });

  return result;
};

const resolveCustomSchema = (
  schemaRef: string,
  customSchemas: SchemaItem[],
): SchemaItem | undefined => {
  const normalizedRef = String(schemaRef);
  return customSchemas.find((schema) => String(schema.id_custom_object) === normalizedRef
    || schema.table_name === normalizedRef
    || schema.schema_name === normalizedRef);
};

const fetchCustomRecord = async (
  relation: CustomObjectRelationDefinition,
  relationValue: string | number,
  customSchemas: SchemaItem[],
): Promise<Record<string, unknown> | null> => {
  const targetSchema = resolveCustomSchema(relation.targetSchema, customSchemas);
  const targetSchemaId = targetSchema?.id_custom_object || Number(relation.targetSchema);
  if (!targetSchemaId || Number.isNaN(targetSchemaId)) return null;

  const query = {
    limit: 10,
    offset: 0,
    filters: [
      {
        field: relation.targetField,
        operator: SpmFilterOperator.EQUALS,
        values: relationValue,
      },
    ],
  };

  const response = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/custom-data-records/${targetSchemaId}`,
    {},
    '',
    undefined,
    true,
    query,
  );

  const normalized = normalizeRecord(response);
  if (normalized) return normalized;

  const fallbackResponse = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    `/custom-data-records/${targetSchemaId}`,
    {},
    '',
    undefined,
    true,
    { limit: 50, offset: 0 },
  );

  if (!fallbackResponse?.data || !Array.isArray(fallbackResponse.data)) return null;
  const matchedRecord = fallbackResponse.data.find(
    (record: Record<string, unknown>) => String(record?.[relation.targetField]) === String(relationValue),
  );
  return isPlainObject(matchedRecord) ? matchedRecord : null;
};

const fetchSystemRecord = async (
  relation: CustomObjectRelationDefinition,
  relationValue: string | number,
): Promise<Record<string, unknown> | null> => {
  const schemaName = relation.targetSchema as SystemRelationSchemaName;
  const schemaDefinition = getSystemRelationSchemaDefinition(schemaName);
  if (!schemaDefinition) return null;

  if (schemaName === 'contacts') {
    const targetField = relation.targetField || 'id_contact';

    if (targetField === 'group_id') {
      // Cible attribut « groupe » : la valeur est l'id externe du groupe (BO
      // du marchand) — la preview montre le groupe du référentiel.
      const response = await nestCall(
        'get',
        NestApiType.CLIENT_API,
        '/customers-groups',
        {},
        '',
        undefined,
        true,
        {
          limit: 10,
          offset: 0,
          filters: [{
            field: 'group_id',
            operator: SpmFilterOperator.EQUALS,
            values: relationValue,
          }],
        },
      );
      return normalizeRecord(response);
    }

    if (targetField !== 'id_contact') {
      // Autres cibles attribut (lang, country…) : la valeur EST le code —
      // aucun « record » à prévisualiser.
      return null;
    }

    const response = await nestCall(
      'get',
      NestApiType.CLIENT_API,
      schemaDefinition.endpoint,
      {},
      '',
      undefined,
      true,
      {
        id_shop_contact: String(relationValue),
        fields: [
          'id_shop_contact',
          'contact_first_name',
          'contact_last_name',
          'contact_data_email',
          'contact_lang',
          'created_at',
          'updated_at',
        ],
      },
    );
    return normalizeRecord(response);
  }

  const response = await nestCall(
    'get',
    NestApiType.CLIENT_API,
    schemaDefinition.endpoint,
    {},
    '',
    undefined,
    true,
    {
      limit: 10,
      offset: 0,
      filters: [{
        field: schemaDefinition.idField,
        operator: SpmFilterOperator.EQUALS,
        values: relationValue,
      }],
    },
  );

  return normalizeRecord(response);
};

export const resolveRelationPreview = async ({
  relation,
  relationValue,
  customSchemas,
  forceRefresh = false,
}: ResolveRelationPreviewParams): Promise<RelationPreviewResult | null> => {
  if (relationValue === null || relationValue === undefined || String(relationValue).trim() === '') {
    return null;
  }

  const cacheKey = [
    relation.targetSchemaType,
    relation.targetSchema,
    relation.targetField,
    String(relationValue),
  ].join(':');

  if (forceRefresh) {
    relationPreviewCache.delete(cacheKey);
  }

  if (relationPreviewCache.has(cacheKey)) {
    return relationPreviewCache.get(cacheKey) || null;
  }

  const targetRecord = relation.targetSchemaType === 'custom'
    ? await fetchCustomRecord(relation, relationValue, customSchemas)
    : await fetchSystemRecord(relation, relationValue);

  if (!targetRecord) {
    relationPreviewCache.set(cacheKey, null);
    return null;
  }

  const flattenedRecord = flattenRecord(targetRecord);

  const customSchema = relation.targetSchemaType === 'custom'
    ? resolveCustomSchema(relation.targetSchema, customSchemas)
    : undefined;
  const systemSchema = relation.targetSchemaType === 'system'
    ? getSystemRelationSchemaDefinition(relation.targetSchema)
    : undefined;

  // Remove all non-date meta keys for custom objects (e.g. __meta_md5)
  if (relation.targetSchemaType === 'custom') {
    Object.keys(flattenedRecord).forEach((key) => {
      if (isMetaKey(key) && !isAllowedCustomMetaKey(key)) {
        delete flattenedRecord[key];
      }
    });
  }

  const fieldLabelMap = buildCustomFieldLabelMap(customSchema);
  const fieldTypeMap = buildCustomFieldTypeMap(customSchema);
  const fields = relation.targetSchemaType === 'custom'
    ? pickCustomPreviewFields(flattenedRecord, relation, customSchema, fieldLabelMap, fieldTypeMap)
    : pickSystemPreviewFields(flattenedRecord, relation, fieldLabelMap);

  const title = relation.targetSchemaType === 'custom'
    ? `${customSchema?.schema_name || relation.targetSchema}`
    : `${systemSchema?.label || toLabel(relation.targetSchema)}`;

  const result: RelationPreviewResult = {
    title,
    fields,
  };

  relationPreviewCache.set(cacheKey, result);
  return result;
};
